import { useIntl } from 'react-intl';

import { useModal } from '~/eds/src/components/hooks/useModal';
import { Text } from '~/eds/src/components/Text/Text';
import { WorkflowPublishActionType } from '~/enums/WorkflowPublishActionType';
import { WorkflowPublishCategoryType } from '~/enums/WorkflowPublishCategoryType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { Nullable } from '~/types/Nullable';
import { Workflow } from '~/types/Workflow';

interface WorkflowActionProps {
  workflow: Nullable<Workflow>;
  action: Nullable<WorkflowPublishActionType | 'DELETE'>;
  onHide: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

export const useWorkflowActionModal = ({
  workflow,
  action,
  onHide,
  onConfirm,
  isLoading,
}: WorkflowActionProps) => {
  const intl = useIntl();
  const isWorkflowRoutingAgentEnabled = useFlag(FlagType.WorkflowRoutingAgent);

  const checkIfUnpublishAction = () =>
    action === WorkflowPublishActionType.Unpublish;

  const checkIfDeleteWorkflowAction = () => action === 'DELETE';

  const getWorkflowActionType = () => {
    if (!workflow) {
      return null;
    }
    const prefilledFormURLExists = !!workflow.prefilledFormData;
    const unpublishAction = checkIfUnpublishAction();

    if (action === 'DELETE') {
      return 'DELETE';
    }

    if (prefilledFormURLExists && !unpublishAction) {
      return WorkflowPublishCategoryType.OveridePublish;
    } else if (prefilledFormURLExists && unpublishAction) {
      return WorkflowPublishCategoryType.OverideUnpublish;
    } else if (!prefilledFormURLExists && unpublishAction) {
      return WorkflowPublishCategoryType.RegularUnpublish;
    } else {
      return WorkflowPublishCategoryType.RegularPublish;
    }
  };

  const workflowActionType = getWorkflowActionType();

  const routingTableSuffixMessages = {
    delete: {
      id: 'UseWorkflowActionModal.routingTableDeleteSuffix',
      defaultMessage:
        'As for the routing table, if an entry exists, it will automatically be deleted.',
      description:
        'Suffix for delete confirmation when workflow routing agent is enabled',
    },
    publish: {
      id: 'UseWorkflowActionModal.routingTablePublishSuffix',
      defaultMessage:
        'As for the routing table, if an entry exists, it will automatically be made Active.',
      description:
        'Suffix for publish confirmation when workflow routing agent is enabled',
    },
    unpublish: {
      id: 'UseWorkflowActionModal.routingTableUnpublishSuffix',
      defaultMessage:
        'As for the routing table, if an entry exists, it will automatically be made inactive.',
      description:
        'Suffix for unpublish confirmation when workflow routing agent is enabled',
    },
  } as const;

  const getRoutingTableSuffix = (
    type: keyof typeof routingTableSuffixMessages,
  ) =>
    isWorkflowRoutingAgentEnabled ? (
      <> {intl.formatMessage(routingTableSuffixMessages[type])}</>
    ) : null;

  const getPublishUnpublishContent = () => {
    switch (workflowActionType) {
      case WorkflowPublishCategoryType.OverideUnpublish:
        return (
          <>
            {intl.formatMessage({
              id: 'UseWorkflowActionModal.overrideUnpublishMessage',
              defaultMessage:
                'Are you sure you want to unpublish this workflow? Please note that:',
              description:
                'Warning message for unpublishing workflow with prefilled form',
            })}
            <br />
            <ul>
              <li>
                {intl.formatMessage({
                  id: 'UseWorkflowActionModal.noNewTicketsMessage',
                  defaultMessage:
                    'Users will not be able to create new tickets based on this workflow.',
                  description:
                    'Message explaining users cannot create new tickets',
                })}
              </li>
              <li>
                {intl.formatMessage({
                  id: 'UseWorkflowActionModal.prefilledFormClearMessage',
                  defaultMessage:
                    'Your Prefillable Form Link will be cleared. Upon republishing, you will need to re-map and re-generate your Prefillable Form Link in the Settings tab of Workflow Builder.',
                  description:
                    'Message about prefilled form link being cleared',
                })}
              </li>
            </ul>
            {intl.formatMessage({
              id: 'UseWorkflowActionModal.pendingTicketsNotAffected',
              defaultMessage:
                'Pending tickets from this workflow will not be effected.',
              description: 'Message about pending tickets not being affected',
            })}
            {getRoutingTableSuffix('unpublish')}
          </>
        );
      case WorkflowPublishCategoryType.RegularUnpublish:
        return (
          <Text>
            {intl.formatMessage({
              id: 'UseWorkflowActionModal.regularUnpublishMessage',
              defaultMessage:
                'Are you sure you want to unpublish this workflow? Users will not be able to create new tickets based on this workflow.',
              description: 'Regular unpublish confirmation message',
            })}{' '}
            <Text variant="body-bold">
              {intl.formatMessage({
                id: 'UseWorkflowActionModal.pendingTicketsNotAffectedBold',
                defaultMessage: 'Any pending tickets will not be affected.',
                description:
                  'Bold text about pending tickets not being affected',
              })}
            </Text>
            {getRoutingTableSuffix('unpublish')}
          </Text>
        );
      case WorkflowPublishCategoryType.OveridePublish:
        return (
          <>
            {intl.formatMessage({
              id: 'UseWorkflowActionModal.overridePublishMessage',
              defaultMessage:
                'Republishing the workflow will necessitate re-mapping and re-generating your Prefillable Form Link in Settings.',
              description:
                'Message about re-mapping prefilled form link when republishing',
            })}
            {getRoutingTableSuffix('publish')}
          </>
        );
      case 'DELETE':
        return (
          <Text>
            {intl.formatMessage(
              {
                id: 'UseWorkflowActionModal.deleteConfirmationMessage',
                defaultMessage:
                  'Are you sure you want to delete {workflowName} Workflow?',
                description:
                  'Confirmation message for deleting a workflow. workflowName is the name of the workflow being deleted (e.g., "Customer Onboarding", "Contract Review")',
              },
              { workflowName: workflow?.name },
            )}
            &nbsp;
            <Text variant="body-bold">
              {intl.formatMessage({
                id: 'UseWorkflowActionModal.deleteCannotRevertMessage',
                defaultMessage: 'This action cannot be reverted.',
                description: 'Warning that delete action cannot be undone',
              })}
            </Text>
            {getRoutingTableSuffix('delete')}
          </Text>
        );
      default:
        return (
          <>
            {intl.formatMessage({
              id: 'UseWorkflowActionModal.defaultPublishMessage',
              defaultMessage:
                'Are you sure you want to publish this workflow? It will be available to users and they will be able to create new tickets based on this workflow.',
              description: 'Default publish confirmation message',
            })}
            {getRoutingTableSuffix('publish')}
          </>
        );
    }
  };

  function getActionText() {
    switch (workflowActionType) {
      case WorkflowPublishCategoryType.OveridePublish:
        return intl.formatMessage({
          id: 'UseWorkflowActionModal.resetMappingPublishButton',
          defaultMessage: 'Reset Mapping & Publish',
          description: 'Button text for resetting mapping and publishing',
        });
      case WorkflowPublishCategoryType.OverideUnpublish:
        return intl.formatMessage({
          id: 'UseWorkflowActionModal.resetMappingUnpublishButton',
          defaultMessage: 'Reset Mapping & Unpublish',
          description: 'Button text for resetting mapping and unpublishing',
        });
      case WorkflowPublishCategoryType.RegularUnpublish:
        return intl.formatMessage({
          id: 'UseWorkflowActionModal.unpublishWorkflowButton',
          defaultMessage: 'Unpublish Workflow',
          description: 'Button text for unpublishing workflow',
        });
      case 'DELETE':
        return intl.formatMessage({
          id: 'UseWorkflowActionModal.deleteWorkflowButton',
          defaultMessage: 'Delete Workflow',
          description: 'Button text for deleting workflow',
        });
      default:
        return intl.formatMessage({
          id: 'UseWorkflowActionModal.publishWorkflowButton',
          defaultMessage: 'Publish Workflow',
          description: 'Button text for publishing workflow',
        });
    }
  }

  const getModalTitle = () => {
    if (checkIfDeleteWorkflowAction()) {
      return intl.formatMessage({
        id: 'UseWorkflowActionModal.deleteWorkflowTitle',
        defaultMessage: 'Delete Workflow',
        description: 'Modal title for deleting workflow',
      });
    }

    if (checkIfUnpublishAction()) {
      return intl.formatMessage({
        id: 'UseWorkflowActionModal.unpublishWorkflowTitle',
        defaultMessage: 'Unpublish Workflow',
        description: 'Modal title for unpublishing workflow',
      });
    }

    return intl.formatMessage({
      id: 'UseWorkflowActionModal.publishWorkflowTitle',
      defaultMessage: 'Publish Workflow',
      description: 'Modal title for publishing workflow',
    });
  };

  return useModal({
    children: getPublishUnpublishContent(),
    title: getModalTitle(),
    onHide,
    primaryAction: {
      text: getActionText(),
      onClick: onConfirm,
      level: workflowActionType === 'DELETE' ? 'danger' : 'primary',
      isLoading,
    },
  });
};
